import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    inject
} from '@angular/core';

import { Observable, ReplaySubject, of } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { LanguageModule } from '@language';
import {
    ApiFragment,
    BaseContainer,
    ExtendedBaseProfileField,
    ExtendedProfile,
    ExtendedProfileService,
    Profile,
    ProfileFieldScope,
    ProfileHelper,
    ShNotificationComponent,
    ShProfileFormModule
} from '@stylehaven/components';
export type ProfileExtras = Record<string, Partial<ExtendedBaseProfileField>>;

@Component({
    selector: 'med-generic-readonly-form',
    templateUrl: './generic-readonly-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ShNotificationComponent, LanguageModule, ShProfileFormModule]
})
export class GenericReadonlyFormContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    public readonly record$: ReplaySubject<ApiFragment> = new ReplaySubject<ApiFragment>(1);
    public profiles$: Observable<ExtendedProfile[]>;

    // Injectors
    private readonly profileHelper: ProfileHelper = inject(ProfileHelper);

    @Input() set record(record: ApiFragment) {
        if (!record) {
            return;
        }

        this.record$.next(record);
    }

    @Input() aiProfile?: Profile;
    @Input() limitToPublicFields = false; // Only true in portal

    @Output() visibleProfiles: EventEmitter<Profile[]> = new EventEmitter<Profile[]>();

    constructor(private profileService: ExtendedProfileService) {
        super();
        this.initProfiles();
    }

    ngOnInit(): void {
        this.setupProfilesEmitter();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        this.record$.complete();
    }

    private setupProfilesEmitter(): void {
        this.profiles$.pipe(takeUntil(this.destroy$)).subscribe(profiles => this.visibleProfiles.emit(profiles));
    }

    private initProfiles(): void {
        this.profiles$ = this.record$.pipe(
            map(record => record.getProfileIds()),
            switchMap(profileIds => this.profileService.getExtended(profileIds)),
            switchMap(profiles => this.filterProfilesByScope(profiles, this.limitToPublicFields))
        );
    }

    private filterProfilesByScope(profiles: ExtendedProfile[], onlyPublic: boolean): Observable<ExtendedProfile[]> {
        if (!onlyPublic) {
            return this.profileHelper.filterViewableProfiles(profiles);
        }
        const onlyPublicProfiles: ExtendedProfile[] = profiles.map(profile => ({
            ...profile,
            Fields: this.filterOnlyPublicFields(profile.Fields)
        }));
        return of(onlyPublicProfiles.filter(profile => profile.Fields.length > 0));
    }

    private filterOnlyPublicFields(fields: ExtendedBaseProfileField[]): ExtendedBaseProfileField[] {
        return fields.filter(field => field.Public === true || field.Scope === ProfileFieldScope.Public);
    }
}
